import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link'
type Size = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  fullWidth?: boolean
}

const VARIANT: Record<Variant, string> = {
  primary:
    // Subtle gradient + ring shadow — modern emelt érzet
    'bg-gradient-to-b from-brand-500 to-brand-600 text-white shadow-soft ' +
    'hover:from-brand-600 hover:to-brand-700 hover:shadow-card ' +
    'active:from-brand-700 active:to-brand-800 ' +
    'disabled:from-brand-600/50 disabled:to-brand-600/50 disabled:shadow-none',
  secondary:
    'bg-white border border-slate-200 text-slate-800 shadow-soft hover:bg-slate-50 hover:border-slate-300 ' +
    'disabled:bg-slate-100 disabled:text-slate-400 ' +
    'dark:bg-slate-800/60 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800 dark:disabled:bg-slate-900',
  ghost:
    'bg-transparent text-slate-700 hover:bg-slate-100/70 ' +
    'dark:text-slate-300 dark:hover:bg-slate-800/60',
  danger:
    'bg-gradient-to-b from-red-500 to-red-600 text-white shadow-soft hover:from-red-600 hover:to-red-700 ' +
    'disabled:opacity-50 disabled:shadow-none',
  link:
    'bg-transparent text-brand-600 hover:text-brand-700 hover:underline underline-offset-2 px-0 ' +
    'dark:text-brand-300 dark:hover:text-brand-200',
}

const SIZE: Record<Size, string> = {
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-9 px-4 text-sm',
  lg: 'h-11 px-5 text-sm',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading,
    leftIcon,
    rightIcon,
    fullWidth,
    className,
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        // Base: gap, alignment, radius, transition, focus
        'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium',
        'transition-all duration-150 ease-out-soft',
        'disabled:cursor-not-allowed',
        // Subtle press feedback
        'active:scale-[0.98]',
        VARIANT[variant],
        variant !== 'link' && SIZE[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? <Spinner /> : leftIcon}
      <span className="truncate">{children}</span>
      {!loading && rightIcon}
    </button>
  )
})

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
    </svg>
  )
}
